import { captureException } from "@sentry/react";
import type { AuthSurface } from "./firebase";
import { SENTRY_ENABLED, sentryLogger } from "./sentry";
import { ws } from "./websocket-wrapper";

/**
 * `/app` and `/operations` hold independent auth sessions (see `firebase.ts`),
 * but a tab has only ONE `ws`, one `core-store` and one `data-cache-store`. This
 * module is the arbiter. Exactly one surface *owns* the socket at a time, and
 * ownership transfers when the user moves between surfaces.
 *
 * Concurrency is therefore per-TAB, which is all the routes can express anyway.
 * `/app` and `/operations` are siblings, so only one is ever mounted. Two tabs
 * genuinely run two sessions side by side (that is the point of the split
 * persistence namespaces); within a single tab, navigating hands the socket over.
 *
 * Ownership has TWO axes, because on `/app` "which session" and "which user" are
 * not the same question. A surface handoff is `claimSocket`; an identity change
 * WITHIN one surface is `releaseForeignSocket`. The second axis exists because an
 * LTI launch signs a second account in on top of a restored one without changing
 * surface, and `ws.connect()` returns early on a socket that is already open OR
 * still handshaking, so the freshly minted token went into `wsUrl` unused and the
 * tab kept serving the PREVIOUS user's connection, resolving every procedure
 * (`workspace.list` included) against them.
 *
 * The `ws` event listeners and `beforeReconnect` are registered here exactly
 * ONCE, at module load. They used to live in the `auth-store` factory body,
 * which only worked while there was a single store. A second store would have
 * clobbered `beforeReconnect` (it is a setter, not a listener list) and left
 * reconnects minting tokens from the wrong session.
 */

export type SurfaceSocketHooks = {
	/** Whether this session currently has a signed-in user. */
	hasUser: () => boolean;
	/** Full token exchange + `ws.connect()`. */
	connect: () => Promise<void>;
	/** Token exchange + `ws.updateUrl` only, the `beforeReconnect` body. */
	refreshUrl: () => Promise<void>;
	setWsConnected: (connected: boolean) => void;
	/** Push (or clear) this surface's identity on the global Sentry scope. */
	syncSentryUser: () => void;
};

const surfaces = new Map<AuthSurface, SurfaceSocketHooks>();

/**
 * Which surface the current URL belongs to. Seeding ownership from the URL (as
 * opposed to defaulting to `"app"` and letting the route correct it) keeps a cold
 * load straight into `/operations` from connecting the `/app` session first.
 * `subscribeToAuthChanges` fires during module init, well before the route's
 * claim effect, so without this the tab would open a socket as the wrong user,
 * registering a connection and a presence event for them, only to tear it down a
 * moment later.
 */
const detectInitialSurface = (): AuthSurface => {
	if (typeof window === "undefined") {
		return "app";
	}
	// The desktop build uses hash history; the web build uses paths.
	const { pathname, hash } = window.location;
	const route = hash.startsWith("#/") ? hash.slice(1) : pathname;
	if (route.startsWith("/operations")) return "operations";
	if (route.startsWith("/organization")) return "organization";
	// Everything else, `/platform` included, is the `/app` session.
	return "app";
};

let activeSurface: AuthSurface = detectInitialSurface();

/**
 * Bumped on every claim so an in-flight connect from a superseded claim can bail
 * instead of racing the new owner onto the socket (same guard shape as
 * `core-store`'s `loadGeneration`).
 */
let claimGeneration = 0;

// Which USER the socket carries.
// `urlUid` is whose credential the current socket URL holds; `socketUid` is whose
// credential the socket that actually OPENED holds. They differ in exactly the
// case this split exists for. A token minted for a new user while the previous
// user's socket is still open makes `connect()` no-op and wastes the mint.
// Only an `open` event promotes one to the other, so `socketUid` never claims an
// identity the server did not actually authenticate.
//
// `urlUid` therefore has to describe the socket currently being opened, not just
// the last mint. That holds only because `socketBelongsToAnotherUser` is
// consulted BEFORE a mint that would overwrite it (`create-auth-store`'s
// `handleSignedIn`). Every other caller of `refreshSocketUrl` re-mints for the
// same user.
let urlUid: string | null = null;
let socketUid: string | null = null;

/**
 * Record whose credential a freshly minted socket URL carries. Called by the
 * auth store right after the token exchange, before `connect()`.
 */
export const noteSocketUrlIdentity = (uid: string | null): void => {
	urlUid = uid;
};

/**
 * Is there a socket in this tab, open, opening, or lately closed, belonging to
 * somebody other than `uid`?
 *
 * Deliberately true for a CLOSED socket that was last opened for another user.
 * Their `core-store` and cache contents are still in this tab, and dropping them
 * is right whether or not the connection happens to be up at this instant.
 *
 * Also true for a socket still HANDSHAKING under another credential, which is a
 * separate reachable state rather than a rounding error. `connect()` returns
 * early on `CONNECTING` exactly as it does on `OPEN` (the two guards at the top
 * of the wrapper's `connect()`), so a mint landing in the handshake window is
 * discarded the same way. Worse, the earlier socket's `open` would then promote
 * the NEWCOMER's `urlUid`, leaving a connection authenticated as one user but
 * recorded as another, with no further release possible. Answering this
 * before the caller mints is what keeps `urlUid` a truthful description of the
 * socket being opened.
 */
export const socketBelongsToAnotherUser = (uid: string): boolean => {
	if (socketUid !== null && socketUid !== uid) {
		return true;
	}
	return ws.isConnecting && urlUid !== null && urlUid !== uid;
};

/**
 * Give up a socket held under another user's credential: close it, drop what was
 * queued for them, and clear the socket-scoped stores. Same teardown
 * `claimSocket` performs for a surface handoff, for the same reason. The next
 * `connect()` must start from nothing of theirs.
 *
 * Leaves `ui-store` alone, exactly as `claimSocket` does. On a launch that is the
 * caller's job (`lib/lti/launch-flow.ts`), which knows an account switch from a
 * teacher relaunching the same course.
 */
export const releaseForeignSocket = async (): Promise<void> => {
	ws.disconnect();
	ws.clearMessageQueue();
	ws.clearPendingCalls();
	// Both axes. The socket is closed (or its handshake aborted), so neither the
	// opened identity nor the URL's pending one describes anything any more. Leaving
	// `urlUid` set would report the same socket foreign a second time and tear the
	// incoming user's own state down under them.
	socketUid = null;
	urlUid = null;
	await resetSocketScopedStores();
};

export const getActiveSurface = (): AuthSurface => activeSurface;

export const isSocketOwner = (surface: AuthSurface): boolean =>
	activeSurface === surface;

export const registerSurface = (
	surface: AuthSurface,
	hooks: SurfaceSocketHooks
): void => {
	surfaces.set(surface, hooks);
};

ws.addEventListener("open", () => {
	// The socket that opened carries the token the URL held. Record WHOSE, so a
	// later sign-in can tell "already connected" from "connected as somebody else".
	socketUid = urlUid;
	console.log("[WebSocket Client] Connected");
	sentryLogger.info("WebSocket connected", { surface: activeSurface });
	surfaces.get(activeSurface)?.setWsConnected(true);
});

ws.addEventListener("close", (event: Event) => {
	const closeEvent = event as CloseEvent;
	console.log(
		`[WebSocket Client] Disconnected (code: ${closeEvent.code}, reason: ${closeEvent.reason})`
	);
	sentryLogger.warn("WebSocket disconnected", {
		code: closeEvent.code,
		reason: closeEvent.reason,
		surface: activeSurface,
	});
	surfaces.get(activeSurface)?.setWsConnected(false);
});

// Every reconnect re-mints a token from whichever session owns the socket *now*,
// not whichever one owned it when the listener was installed. Throwing here makes
// `scheduleReconnect` retry rather than connect with a stale/foreign token.
ws.beforeReconnect = async () => {
	const hooks = surfaces.get(activeSurface);
	if (!hooks) {
		throw new Error(`No auth session registered for surface ${activeSurface}`);
	}
	if (!hooks.hasUser()) {
		throw new Error(`No user signed in on the ${activeSurface} session`);
	}
	console.log("[WebSocket Client] Refreshing auth token before reconnect...");
	await hooks.refreshUrl();
};

/**
 * Data belonging to whoever last held the socket. Deliberately does NOT touch
 * `ui-store`, which holds the `/app` session's unsent drafts, grid layout and
 * workspace selection. Wiping it on an ops handoff would destroy another
 * account's work in a live `/app` tab.
 */
const resetSocketScopedStores = async (): Promise<void> => {
	const [{ coreStore }, { dataCacheStore }] = await Promise.all([
		import("./stores/core-store"),
		import("./stores/data-cache-store"),
	]);
	coreStore.getState().reset();
	dataCacheStore.getState().reset();
};

/**
 * Hand the socket to `surface`. No-op when it already owns it, so a remount or a
 * re-entry costs nothing. Otherwise: tear the socket down, drop anything queued
 * for the old session, wipe the socket-scoped stores, then connect the new
 * session if it has a user (if it doesn't, its route renders a login and the
 * connect happens from its auth-state handler once someone signs in).
 */
export const claimSocket = async (surface: AuthSurface): Promise<void> => {
	if (activeSurface === surface) {
		return;
	}

	const previous = activeSurface;
	claimGeneration += 1;
	const generation = claimGeneration;
	// Flip ownership synchronously, before any await. A `beforeReconnect` or
	// auth-state change landing in the await window below must already see the new
	// owner, or the outgoing session would quietly reconnect itself.
	activeSurface = surface;

	ws.disconnect();
	ws.clearMessageQueue();
	ws.clearPendingCalls();
	surfaces.get(previous)?.setWsConnected(false);

	await resetSocketScopedStores();
	if (claimGeneration !== generation) {
		return;
	}

	const hooks = surfaces.get(surface);
	hooks?.syncSentryUser();
	if (!hooks?.hasUser()) {
		return;
	}

	try {
		await hooks.connect();
	} catch (error) {
		if (claimGeneration !== generation) {
			return;
		}
		console.error("[WebSocket Client] Failed to connect after claim:", error);
		sentryLogger.error("WebSocket connection failed after socket claim", {
			surface,
		});
		if (SENTRY_ENABLED) {
			captureException(error, { tags: { context: "socket_claim_connect" } });
		}
	}
};

/**
 * Test seam: force which surface owns the socket. Deliberately leaves the
 * registry intact. Each auth store registers exactly once, at module init, so
 * clearing it would un-wire the real stores for the rest of the file.
 */
export const __test__setActiveSurface = (surface: AuthSurface): void => {
	activeSurface = surface;
	claimGeneration = 0;
	urlUid = null;
	socketUid = null;
};

/** Test seam: pretend a socket opened under `uid`'s credential. */
export const __test__setSocketIdentity = (uid: string | null): void => {
	urlUid = uid;
	socketUid = uid;
};
